import { ensureArchiveFileName } from './archive-file-name';

export type ArchiveSaveMode = 'download' | 'picker';

interface ArchiveFileSystemWritableFileStream {
  write(data: Blob): Promise<void>;
  close(): Promise<void>;
}

interface ArchiveFileHandle {
  createWritable(): Promise<ArchiveFileSystemWritableFileStream>;
}

interface ArchiveFilePickerWindow extends Window {
  showSaveFilePicker?: (options: {
    suggestedName: string;
    excludeAcceptAllOption?: boolean;
    types?: Array<{
      description: string;
      accept: Record<string, string[]>;
    }>;
  }) => Promise<ArchiveFileHandle>;
}

export function canPickArchiveSaveLocation(): boolean {
  return typeof window !== 'undefined' && typeof (window as ArchiveFilePickerWindow).showSaveFilePicker === 'function';
}

export async function saveArchiveBlob(
  blob: Blob,
  options: { fileName: string; mode: ArchiveSaveMode }
): Promise<{ fileName: string; mode: ArchiveSaveMode }> {
  const fileName = ensureArchiveFileName(options.fileName);

  if (options.mode === 'picker' && canPickArchiveSaveLocation()) {
    const handle = await (window as ArchiveFilePickerWindow).showSaveFilePicker?.({
      suggestedName: fileName,
      types: [{
        description: 'JourneyNodes archive',
        accept: { 'application/octet-stream': ['.journeynodes'] },
      }],
    });
    const writable = await handle?.createWritable();

    await writable?.write(blob);
    await writable?.close();

    return { fileName, mode: 'picker' };
  }

  triggerArchiveDownload(blob, fileName);
  return { fileName, mode: 'download' };
}

export function isArchiveSaveCancelled(error: unknown): boolean {
  return (error instanceof DOMException && error.name === 'AbortError') || (error instanceof Error && error.name === 'AbortError');
}

function triggerArchiveDownload(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}